// src/components/atomic-crm/notes/NoteCreate.tsx
import {
  CreateBase,
  Form,
  useGetIdentity,
  useRefresh,
  useNotify,
  useRecordContext,
  useUpdate,
  useTranslate,
  type Identifier,
  type RaRecord,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { SaveButton } from "@/components/ds/admin/form";
import { cn } from "@/lib/utils";

import { NoteInputs } from "./NoteInputs";
import { getCurrentDate } from "./utils";

const foreignKeyMapping = {
  contacts: "contact_id",
  deals: "deal_id",
  companies: "company_id",
  tasks: "deal_id", // 👈 tasks are gone; keep as a safe fallback
  invoices: "invoice_id", // 👈 invoice notes still live in invoice_notes
} as const;

type NoteReference = keyof typeof foreignKeyMapping;

export const NoteCreate = ({
  reference,
  showStatus,
  className,
}: {
  reference: NoteReference;
  showStatus?: boolean;
  className?: string;
}) => {
  const record = useRecordContext();
  const { identity } = useGetIdentity();

  if (!record || !identity) return null;

  // Always write to crm_activities. The `reference` prop only decides
  // which FK we set (company_id / contact_id / deal_id).
  const targetResource =
    reference === "invoices" ? "invoice_notes" : "crm_activities";

  return (
    <CreateBase resource={targetResource} redirect={false}>
      <Form>
        <div className={cn("space-y-3", className)}>
          <NoteInputs showStatus={showStatus} />
          <NoteCreateButton reference={reference} record={record} />
        </div>
      </Form>
    </CreateBase>
  );
};

const NoteCreateButton = ({
  reference,
  record,
}: {
  reference: NoteReference;
  record: RaRecord<Identifier>;
}) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { reset } = useFormContext();
  const refresh = useRefresh();
  const translate = useTranslate();

  if (!record || !identity) return null;

  const resetValues: {
    date: string;
    text: null;
    attachments: null;
    status?: string;
  } = {
    date: getCurrentDate(),
    text: null,
    attachments: null,
  };

  if (reference === "contacts") {
    resetValues.status = "warm";
  }

  const handleSuccess = (data: any) => {
    reset(resetValues, { keepValues: false });
    refresh();

    // Only update last_seen for resources that have this column
    const updateData: any = {};
    if (reference === "contacts" || reference === "companies") {
      updateData.last_seen = new Date().toISOString();
    }
    if (reference === "contacts") {
      updateData.status = data.status;
    }

    if (Object.keys(updateData).length > 0) {
      update(reference, {
        id: record.id as Identifier,
        data: updateData,
        previousData: record,
      });
    }
    notify(translate("crm.note.added"));
  };

  return (
    <div className="flex justify-end">
      <SaveButton
        type="button"
        label={translate("crm.note.action.add")}
        transform={(data) => {
          // Invoice notes still use their own table
          if (reference === "invoices") {
            return {
              invoice_id: record.id,
              text: data.text,
              date: data.date || getCurrentDate(),
              sales_id: identity.id,
            };
          }

          // Everything else writes to crm_activities
          return {
            type: "note",
            text: data.text,
            date: data.date || getCurrentDate(),
            status: data.status ?? null,
            [foreignKeyMapping[reference]]: record.id,
            sales_id: identity.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        }}
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      >
        {translate("crm.note.action.add")}
      </SaveButton>
    </div>
  );
};
